import ExtendoError from 'extendo-error';
import { normalize } from 'normalizr';
import { SuperAgentStatic } from 'superagent';
import { SPAREMIN_SERVICES_CREATION_URL } from 'config';
import { attachFields } from 'utils/request';
import { createRequest } from '../utils';
import {
  creationClipSuggestionSchema,
  creationClipSuggestionsSchema,
  creationSchema,
  creationSuggestionsProgressSchema,
  videoSocialPostCaptionsSchema,
  videoZipRequestsByIdSchema,
} from './schemas';
import {
  ACTION_KEY,
  AssetStatus,
  CreateCreationAssetArgs,
  CreateCreationAssetResponse,
  CreateCreationClipSuggestionArgs,
  CreateCreationClipSuggestionFeedbackArgs,
  CreateCreationClipSuggestionResult,
  CreateSocialPostCaptionsArgs,
  CreateSocialPostCaptionsResult,
  CreationArgs,
  CreationResult,
  EnableCreationClipSuggestionArgs,
  EnableCreationClipSuggestionResult,
  GenerateProjectNameArgs,
  GetCreationArgs,
  GetCreationClipSuggestionArgs,
  GetCreationClipSuggestionResult,
  GetCreationClipSuggestionsArgs,
  GetCreationClipSuggestionsProgressResult,
  GetCreationClipSuggestionsResult,
  GetProjectLatestRegenerationArgs,
  GetProjectNameGenerationArgs,
  GetProjectRegenerationArgs,
  GetSocialPostCaptionsArgs,
  GetSocialPostCaptionsJobArgs,
  GetSocialPostCaptionsResult,
  NormalizeVideoZipRequestResult,
  ProjectNameGeneration,
  ProjectRegeneration,
  ReceiveCreationSuggestionEventArgs,
  ReceiveCreationSuggestionEventResult,
  RegenerateProjectArgs,
  ReprocessCreationSuggestionArgs,
  ServiceMethod,
  VideoZipRequestArgs,
} from './types';

const BASE_PATH = '/api/v1/creation';
const CREATION_REQUEST_PATH = `${BASE_PATH}/creation-request`;
const PROJECT_REGENERATION_REQUEST_PATH = `${BASE_PATH}/project-regeneration-request`;
const PROJECT_NAME_GENERATION_REQUEST_PATH = `${BASE_PATH}/project-name-generation-request`;
const VIDEO_ZIP_REQUEST_PATH = `${BASE_PATH}/video-group-zip-request`;

class ZipNotFoundError extends ExtendoError {}

async function create(
  request: SuperAgentStatic,
  [creationReq]: CreationArgs,
): Promise<CreationResult> {
  const req = request.post(CREATION_REQUEST_PATH);
  attachFields(req, creationReq);

  const { body } = await req;
  return normalize(body, creationSchema);
}

async function getCreation(
  request: SuperAgentStatic,
  [creationId]: GetCreationArgs,
): Promise<CreationResult> {
  const req = request.get(`${CREATION_REQUEST_PATH}/${creationId}`);
  const { body } = await req;
  return normalize(body, creationSchema);
}

async function enableCreationClipSuggestion(
  request: SuperAgentStatic,
  [creationRequestId, suggestionId]: EnableCreationClipSuggestionArgs,
): Promise<EnableCreationClipSuggestionResult> {
  const res = await request.post(
    `${CREATION_REQUEST_PATH}/${creationRequestId}/suggestion/${suggestionId}/enable`,
  );

  return res.body;
}

async function receiveCreationSuggestionEvent(
  request: SuperAgentStatic,
  {
    creationRequestId,
    suggestionId,
    ...params
  }: ReceiveCreationSuggestionEventArgs,
): Promise<ReceiveCreationSuggestionEventResult> {
  await request
    .post(
      `${CREATION_REQUEST_PATH}/${creationRequestId}/suggestion/${suggestionId}/event/receive`,
    )
    .send(params);

  return null;
}

async function createCreationClipSuggestionFeedback(
  request: SuperAgentStatic,
  [
    feedbackType,
    creationRequestId,
    suggestionId,
  ]: CreateCreationClipSuggestionFeedbackArgs,
) {
  await request
    .post(
      `${CREATION_REQUEST_PATH}/${creationRequestId}/suggestion/${suggestionId}/feedback`,
    )
    .send({
      feedbackType,
      creationRequestId,
      suggestionId,
    });
}

async function getCreationClipSuggestions(
  request: SuperAgentStatic,
  [creationRequestId]: GetCreationClipSuggestionsArgs,
): Promise<GetCreationClipSuggestionsResult> {
  const res = await request.get(
    `${CREATION_REQUEST_PATH}/${creationRequestId}/suggestion`,
  );

  return normalize(res.body, creationClipSuggestionsSchema);
}

const getCreationClipSuggestion = async (
  request: SuperAgentStatic,
  args: GetCreationClipSuggestionArgs,
): Promise<GetCreationClipSuggestionResult> => {
  const [creationRequestId, suggestionId] = args;

  const { body } = await request.get(
    `${CREATION_REQUEST_PATH}/${creationRequestId}/suggestion/${suggestionId}`,
  );

  return normalize(body, creationClipSuggestionSchema);
};

const getCreationSuggestionsProgress = async (
  request: SuperAgentStatic,
  args: GetCreationClipSuggestionArgs,
): Promise<GetCreationClipSuggestionsProgressResult> => {
  const [creationRequestId] = args;

  const res = await request.get(
    `${CREATION_REQUEST_PATH}/${creationRequestId}/suggestion-progress`,
  );

  return normalize(res, [creationSuggestionsProgressSchema]);
};

async function createCreationClipSuggestion(
  request: SuperAgentStatic,
  [
    creationRequestId,
    replaceSuggestionId,
    newTrimStartMillis,
    newTrimEndMillis,
  ]: CreateCreationClipSuggestionArgs,
): Promise<CreateCreationClipSuggestionResult> {
  const res = await request
    .post(`${CREATION_REQUEST_PATH}/${creationRequestId}/suggestion`)
    .send({ replaceSuggestionId, newTrimStartMillis, newTrimEndMillis });

  return res.body;
}

async function reprocessCreationClipSuggestion(
  request: SuperAgentStatic,
  {
    creationRequestId,
    suggestionId,
    ...params
  }: ReprocessCreationSuggestionArgs,
) {
  await request
    .post(
      `${CREATION_REQUEST_PATH}/${creationRequestId}/suggestion/${suggestionId}/reprocess`,
    )
    .send(params);

  return undefined;
}

const createSocialPostCaptions = (
  request: SuperAgentStatic,
  args: CreateSocialPostCaptionsArgs,
): Promise<CreateSocialPostCaptionsResult> => {
  const [creationRequestId] = args;

  return request
    .post(
      `${CREATION_REQUEST_PATH}/${creationRequestId}/social-post-caption-job`,
    )
    .then(res => res.body);
};

async function getSocialPostCaptions(
  request: SuperAgentStatic,
  [creationRequestId]: GetSocialPostCaptionsArgs,
): Promise<GetSocialPostCaptionsResult> {
  const res = await request.get(
    `${CREATION_REQUEST_PATH}/${creationRequestId}/social-post-caption-job`,
  );

  return normalize(res.body, videoSocialPostCaptionsSchema);
}

const getSocialPostCaptionsJob = (
  request: SuperAgentStatic,
  args: GetSocialPostCaptionsJobArgs,
): Promise<GetSocialPostCaptionsResult> => {
  const [creationRequestId, jobId] = args;

  return request
    .get(
      `${CREATION_REQUEST_PATH}/${creationRequestId}/social-post-caption-job/${jobId}`,
    )
    .then(res => normalize(res.body, videoSocialPostCaptionsSchema));
};

async function regenerateProject(
  request: SuperAgentStatic,
  {
    projectId,
    baseConfigJson,
    captionSourceEddyProjectCuid,
    videoFrameHeight,
    videoFrameWidth,
    videoFrameQuality,
    videoFrameRate,
  }: RegenerateProjectArgs,
): Promise<ProjectRegeneration> {
  const res = await request
    .post(`${PROJECT_REGENERATION_REQUEST_PATH}/${projectId}`)
    .send({
      baseConfigJson,
      captionSourceEddyProjectCuid,
      videoFrameHeight,
      videoFrameWidth,
      videoFrameQuality,
      videoFrameRate,
    });

  return res.body;
}

const getProjectLatestRegeneration = async (
  request: SuperAgentStatic,
  args: GetProjectLatestRegenerationArgs,
): Promise<ProjectRegeneration> => {
  const [projectId] = args;

  const res = await request.get(
    `${PROJECT_REGENERATION_REQUEST_PATH}/${projectId}`,
  );

  return res.body;
};

const getProjectRegeneration = async (
  request: SuperAgentStatic,
  args: GetProjectRegenerationArgs,
): Promise<ProjectRegeneration> => {
  const [projectId, projectRegenerationId] = args;

  const res = await request.get(
    `${PROJECT_REGENERATION_REQUEST_PATH}/${projectId}/request/${projectRegenerationId}`,
  );

  return res.body;
};

const generateProjectName = async (
  request: SuperAgentStatic,
  args: GenerateProjectNameArgs,
): Promise<ProjectNameGeneration> => {
  const { projectUuid, formality, tone } = args;

  const res = await request
    .post(PROJECT_NAME_GENERATION_REQUEST_PATH)
    .send({ projectUuid, formality, tone });

  return res.body;
};

const getProjectNameGeneration = async (
  request: SuperAgentStatic,
  args: GetProjectNameGenerationArgs,
): Promise<ProjectNameGeneration> => {
  const [projectNameGenerationId] = args;

  const res = await request.get(
    `${PROJECT_NAME_GENERATION_REQUEST_PATH}/${projectNameGenerationId}`,
  );

  return res.body;
};

const createCreationAsset = async (
  request: SuperAgentStatic,
  args: CreateCreationAssetArgs,
): Promise<CreateCreationAssetResponse> => {
  const req = request.post(`${BASE_PATH}/creation-request-asset`);
  attachFields(req, args);

  const { body } = await req;

  return body;
};

async function createVideoZipRequest(
  request: SuperAgentStatic,
  args: VideoZipRequestArgs,
): Promise<NormalizeVideoZipRequestResult> {
  const res = await request.post(VIDEO_ZIP_REQUEST_PATH).send(args);

  return normalize(res.body, videoZipRequestsByIdSchema);
}

async function getVideoZipRequest(
  request: SuperAgentStatic,
  args: VideoZipRequestArgs,
): Promise<NormalizeVideoZipRequestResult> {
  const res = await request.get(VIDEO_ZIP_REQUEST_PATH).query(args);

  if (res.body.status === AssetStatus.NOT_FOUND) {
    throw new ZipNotFoundError('Zip not found');
  }

  return normalize(res.body, videoZipRequestsByIdSchema);
}

export const handle = (method: any, args: any, token?: string): any => {
  const request = createRequest({
    token,
    baseUrl: SPAREMIN_SERVICES_CREATION_URL,
  });

  switch (method) {
    case ServiceMethod.CREATE:
      return create(request, args);

    case ServiceMethod.CREATION_GET:
      return getCreation(request, args);

    case ServiceMethod.GET_CREATION_CLIP_SUGGESTIONS:
      return getCreationClipSuggestions(request, args);

    case ServiceMethod.GET_CREATION_CLIP_SUGGESTION:
      return getCreationClipSuggestion(request, args);

    case ServiceMethod.GET_CREATION_CLIP_SUGGESTIONS_PROGRESS:
      return getCreationSuggestionsProgress(request, args);

    case ServiceMethod.ENABLE_CREATION_CLIP_SUGGESTION:
      return enableCreationClipSuggestion(request, args);

    case ServiceMethod.RECEIVE_CREATION_SUGGESTION_EVENT:
      return receiveCreationSuggestionEvent(request, args);

    case ServiceMethod.CREATE_CREATION_CLIP_SUGGESTION_FEEDBACK:
      return createCreationClipSuggestionFeedback(request, args);

    case ServiceMethod.CREATE_CREATION_CLIP_SUGGESTION:
      return createCreationClipSuggestion(request, args);

    case ServiceMethod.REPROCESS_CREATION_SUGGESTION:
      return reprocessCreationClipSuggestion(request, args);

    case ServiceMethod.CREATE_SOCIAL_POST_CAPTIONS:
      return createSocialPostCaptions(request, args);

    case ServiceMethod.GET_SOCIAL_POST_CAPTIONS:
      return getSocialPostCaptions(request, args);

    case ServiceMethod.GET_SOCIAL_POST_CAPTIONS_JOB:
      return getSocialPostCaptionsJob(request, args);

    case ServiceMethod.REGENERATE_PROJECT:
      return regenerateProject(request, args);

    case ServiceMethod.GET_PROJECT_LATEST_REGENERATION:
      return getProjectLatestRegeneration(request, args);

    case ServiceMethod.GET_PROJECT_REGENERATION:
      return getProjectRegeneration(request, args);

    case ServiceMethod.GENERATE_PROJECT_NAME:
      return generateProjectName(request, args);

    case ServiceMethod.GET_PROJECT_NAME_GENERATION:
      return getProjectNameGeneration(request, args);

    case ServiceMethod.CREATE_CREATION_ASSET:
      return createCreationAsset(request, args);

    case ServiceMethod.CREATE_VIDEO_ZIP_REQUEST:
      return createVideoZipRequest(request, args);

    case ServiceMethod.GET_VIDEO_ZIP_REQUEST:
      return getVideoZipRequest(request, args);

    default:
      throw new Error(`${ACTION_KEY} cannot handle method ${method}`);
  }
};
